import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  useListRefinementsQuery,
  useUpsertRefinementMutation,
  useDeleteRefinementMutation,
} from '../redux/firestoreApi';
import { showToast } from '../redux/uiSlice';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import ConfirmDialog from '../components/common/ConfirmDialog';
import type { Refinement } from '../types/models';

interface FormData {
  topic: string;
  notes?: string;
}

export default function RefinementsPage() {
  const uid = useAppSelector((s) => s.session.uid)!;
  const projectId = useAppSelector((s) => s.session.activeProjectId);
  const sprintId = useAppSelector((s) => s.session.activeSprintId);
  const dispatch = useAppDispatch();

  const { data: items = [], isLoading } = useListRefinementsQuery(
    projectId && sprintId ? { uid, projectId, sprintId } : (undefined as any),
    { skip: !projectId || !sprintId },
  );
  const [upsert] = useUpsertRefinementMutation();
  const [remove] = useDeleteRefinementMutation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Refinement | null>(null);
  const [toDelete, setToDelete] = useState<Refinement | null>(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>();

  if (!projectId || !sprintId) {
    return (
      <>
        <PageHeader title="Refinements" />
        <Alert severity="info">Select a project and sprint.</Alert>
      </>
    );
  }

  const openCreate = () => {
    setEditing(null);
    reset({ topic: '', notes: '' });
    setOpen(true);
  };

  const openEdit = (r: Refinement) => {
    setEditing(r);
    reset({ topic: r.topic, notes: r.notes ?? '' });
    setOpen(true);
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      await upsert({
        uid,
        projectId,
        sprintId,
        id: editing?.id,
        ...data,
      }).unwrap();
      dispatch(showToast({ message: editing ? 'Updated' : 'Added', severity: 'success' }));
      setOpen(false);
    } catch (e: any) {
      dispatch(showToast({ message: e?.message ?? 'Save failed', severity: 'error' }));
    }
  });

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await remove({ uid, projectId, sprintId, id: toDelete.id }).unwrap();
      dispatch(showToast({ message: 'Deleted', severity: 'success' }));
    } catch (e: any) {
      dispatch(showToast({ message: e?.message ?? 'Delete failed', severity: 'error' }));
    }
  };

  return (
    <>
      <PageHeader
        title="Refinements"
        subtitle="Discussion topics & backlog grooming items."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New refinement
          </Button>
        }
      />

      {!isLoading && items.length === 0 ? (
        <EmptyState
          title="No refinements"
          description="Track topics you discussed during refinement."
          action={<Button variant="contained" onClick={openCreate}>Add refinement</Button>}
        />
      ) : (
        <Paper variant="outlined">
          <List disablePadding>
            {items.map((r) => (
              <ListItem
                key={r.id}
                divider
                secondaryAction={
                  <>
                    <IconButton size="small" onClick={() => openEdit(r)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => setToDelete(r)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </>
                }
              >
                <ListItemText
                  primary={r.topic}
                  secondary={
                    r.notes ? (
                      <Typography variant="body2" color="text.secondary">
                        {r.notes}
                      </Typography>
                    ) : null
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit refinement' : 'New refinement'}</DialogTitle>
        <form onSubmit={onSubmit}>
          <DialogContent>
            <Stack spacing={2}>
              <TextField
                label="Topic"
                autoFocus
                error={!!errors.topic}
                helperText={errors.topic?.message}
                {...register('topic', { required: 'Required' })}
              />
              <TextField label="Notes" multiline rows={4} {...register('notes')} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editing ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={!!toDelete}
        title="Delete refinement?"
        confirmLabel="Delete"
        destructive
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </>
  );
}
